import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Input,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableShell,
  cn,
} from '@zafroi/ui-kit';
import { Loader2, Mail, Search, X } from 'lucide-react';
import { listSentEmails } from '@/services/engage-api';
import type { SentEmailLog } from '@/types/engage';

/** kind (template key) → friendly label for the Type column + filter. */
const KIND_LABELS: Record<string, string> = {
  INVOICE_ISSUED: 'Invoice',
  INVOICE_REMINDER_UPCOMING: 'Reminder · upcoming',
  INVOICE_REMINDER_DUE: 'Reminder · due',
  INVOICE_REMINDER_OVERDUE: 'Reminder · overdue',
  INVOICE_REMINDER_FINAL: 'Reminder · final',
};
const kindLabel = (k: string) => KIND_LABELS[k] ?? k;
const KIND_OPTIONS = Object.keys(KIND_LABELS);
const PAGE_SIZE = 25;

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * Sent-emails audit log — every outbound email the system sent (invoice emails,
 * dunning reminders, …), searchable + filterable, with delivery status and the
 * SES message-id for tracing.
 */
export function SentEmailsPage() {
  const [recipientInput, setRecipientInput] = useState('');
  const [recipient, setRecipient] = useState('');
  const [kind, setKind] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<SentEmailLog | null>(null);

  // Debounce the recipient search → reset to page 1.
  useEffect(() => {
    const h = setTimeout(() => {
      setRecipient(recipientInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(h);
  }, [recipientInput]);

  const params = useMemo(
    () => ({
      recipient: recipient || undefined,
      kind: kind || undefined,
      from: from ? new Date(from).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [recipient, kind, from, to, page],
  );

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['sent-emails', params],
    queryFn: () => listSentEmails(params),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = !!(recipientInput || kind || from || to);

  function clearFilters() {
    setRecipientInput('');
    setKind('');
    setFrom('');
    setTo('');
    setPage(1);
  }

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-6">
      <header className="mb-5">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <Mail className="h-5 w-5 text-muted-foreground" /> Sent emails
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every email the system sent — invoice notices and payment reminders — with delivery
          status and message-id for tracing.
        </p>
      </header>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={recipientInput}
            onChange={(e) => setRecipientInput(e.target.value)}
            placeholder="Search recipient…"
            className="pl-8"
          />
        </div>
        <label className="flex flex-col gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Type
          <select
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-elev-1 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">All types</option>
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {kindLabel(k)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-elev-1 focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-elev-1 focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-4 w-4" /> Clear
          </Button>
        )}
        <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {total} email{total === 1 ? '' : 's'}
        </span>
      </div>

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[170px]">Date</TableHead>
              <TableHead>To</TableHead>
              <TableHead className="w-[160px]">Type</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead className="w-[170px]">Invoice</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-destructive">
                  Couldn&apos;t load the sent-email log.
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                  {hasFilters ? 'No emails match these filters.' : 'No emails sent yet.'}
                </TableCell>
              </TableRow>
            ) : (
              items.map((e) => (
                <TableRow
                  key={e.id}
                  className="cursor-pointer"
                  onClick={() => setSelected(e)}
                >
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {fmtDateTime(e.createdAt)}
                  </TableCell>
                  <TableCell className="text-sm font-medium text-foreground">{e.recipient}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{kindLabel(e.kind)}</TableCell>
                  <TableCell className="max-w-[420px] truncate text-sm text-foreground">
                    {e.subject}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {e.relatedRef ?? '—'}
                  </TableCell>
                  <TableCell>
                    <StatusPill status={e.status} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableShell>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Detail */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full max-w-md overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Email detail
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <Field label="Status">
                  <StatusPill status={selected.status} />
                </Field>
                <Field label="Sent at">{fmtDateTime(selected.createdAt)}</Field>
                <Field label="To">{selected.recipient}</Field>
                <Field label="Type">{kindLabel(selected.kind)}</Field>
                <Field label="Subject">
                  <span className="text-foreground">{selected.subject}</span>
                </Field>
                {selected.relatedType === 'kosh_invoice' && selected.relatedRef && (
                  <Field label="Invoice">
                    <span className="font-mono text-xs">{selected.relatedRef}</span>
                  </Field>
                )}
                <Field label="Message-ID">
                  <span className="break-all font-mono text-xs text-muted-foreground">
                    {selected.messageId ?? '—'}
                  </span>
                </Field>
                {selected.status === 'FAILED' && selected.error && (
                  <Field label="Error">
                    <span className="break-words text-xs text-destructive">{selected.error}</span>
                  </Field>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatusPill({ status }: { status: SentEmailLog['status'] }) {
  return (
    <Badge tone={status === 'SENT' ? 'success' : 'destructive'} withDot={false} className="text-[10px]">
      {status === 'SENT' ? 'Sent' : 'Failed'}
    </Badge>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={cn('flex flex-col gap-1')}>
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}
